/**
 * 2-D geometry primitives that replace the OpenCV contour/shape ops used by the
 * Python pipeline. Documents are convex quads, so we lean on the convex hull
 * and rotating calipers rather than full contour machinery.
 */

export interface Point {
  x: number;
  y: number;
}

/** A min-area rectangle, mirroring cv2.minAreaRect's (center,(w,h),angle). */
export interface MinAreaRect {
  cx: number;
  cy: number;
  w: number;
  h: number;
  /** Angle in degrees, matching OpenCV's convention (see minAreaRect). */
  angle: number;
}

/** Polygon area via the shoelace formula (= cv2.contourArea for simple polys). */
export function polygonArea(pts: Point[]): number {
  let a = 0;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    a += pts[j].x * pts[i].y - pts[i].x * pts[j].y;
  }
  return Math.abs(a) / 2;
}

/** Perimeter (= cv2.arcLength closed). */
export function arcLength(pts: Point[], closed = true): number {
  let len = 0;
  const n = pts.length;
  for (let i = 0; i < n - 1; i++) len += dist(pts[i], pts[i + 1]);
  if (closed && n > 1) len += dist(pts[n - 1], pts[0]);
  return len;
}

export function dist(a: Point, b: Point): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

/**
 * Andrew's monotone-chain convex hull. Returns the hull in counter-clockwise
 * order (screen coords). Mirrors cv2.convexHull for our purposes.
 */
export function convexHull(points: Point[]): Point[] {
  const pts = points.slice().sort((a, b) => (a.x === b.x ? a.y - b.y : a.x - b.x));
  if (pts.length <= 2) return pts;
  const cross = (o: Point, a: Point, b: Point) =>
    (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
  const lower: Point[] = [];
  for (const p of pts) {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0)
      lower.pop();
    lower.push(p);
  }
  const upper: Point[] = [];
  for (let i = pts.length - 1; i >= 0; i--) {
    const p = pts[i];
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0)
      upper.pop();
    upper.push(p);
  }
  lower.pop();
  upper.pop();
  return lower.concat(upper);
}

/**
 * Minimum-area enclosing rectangle of a convex polygon via rotating calipers —
 * the replacement for cv2.minAreaRect. Returns center, (w,h) and an OpenCV-style
 * angle. We compute the box corners and derive the angle so the deskew "rotate"
 * fallback can use the same -45..45 normalisation as the Python code.
 */
export function minAreaRect(points: Point[]): MinAreaRect {
  const hull = convexHull(points);
  if (hull.length < 3) {
    const xs = points.map((p) => p.x);
    const ys = points.map((p) => p.y);
    const minX = Math.min(...xs), maxX = Math.max(...xs);
    const minY = Math.min(...ys), maxY = Math.max(...ys);
    return {
      cx: (minX + maxX) / 2,
      cy: (minY + maxY) / 2,
      w: maxX - minX,
      h: maxY - minY,
      angle: 0,
    };
  }
  let best: MinAreaRect | null = null;
  let bestArea = Infinity;
  const n = hull.length;
  for (let i = 0; i < n; i++) {
    const p0 = hull[i];
    const p1 = hull[(i + 1) % n];
    const ex = p1.x - p0.x;
    const ey = p1.y - p0.y;
    const len = Math.hypot(ex, ey) || 1;
    const ux = ex / len; // edge direction
    const uy = ey / len;
    // Project all hull points onto edge dir (u) and its normal (v=(-uy,ux)).
    let minU = Infinity, maxU = -Infinity, minV = Infinity, maxV = -Infinity;
    for (const p of hull) {
      const u = p.x * ux + p.y * uy;
      const v = -p.x * uy + p.y * ux;
      if (u < minU) minU = u;
      if (u > maxU) maxU = u;
      if (v < minV) minV = v;
      if (v > maxV) maxV = v;
    }
    const w = maxU - minU;
    const h = maxV - minV;
    const area = w * h;
    if (area < bestArea) {
      bestArea = area;
      // Center in (u,v) → back to (x,y).
      const cu = (minU + maxU) / 2;
      const cv = (minV + maxV) / 2;
      const cx = cu * ux - cv * uy;
      const cy = cu * uy + cv * ux;
      const angle = (Math.atan2(uy, ux) * 180) / Math.PI;
      best = { cx, cy, w, h, angle };
    }
  }
  return best!;
}

/** The 4 corners of a {@link MinAreaRect}, like cv2.boxPoints. */
export function boxPoints(r: MinAreaRect): Point[] {
  const rad = (r.angle * Math.PI) / 180;
  const ux = Math.cos(rad), uy = Math.sin(rad);
  const vx = -Math.sin(rad), vy = Math.cos(rad);
  const hw = r.w / 2, hh = r.h / 2;
  return [
    { x: r.cx - ux * hw - vx * hh, y: r.cy - uy * hw - vy * hh },
    { x: r.cx + ux * hw - vx * hh, y: r.cy + uy * hw - vy * hh },
    { x: r.cx + ux * hw + vx * hh, y: r.cy + uy * hw + vy * hh },
    { x: r.cx - ux * hw + vx * hh, y: r.cy - uy * hw + vy * hh },
  ];
}

/**
 * Douglas–Peucker polyline simplification — the replacement for cv2.approxPolyDP
 * (closed contour). `epsilon` is in pixels, same semantics as OpenCV.
 */
export function approxPolyDP(points: Point[], epsilon: number): Point[] {
  if (points.length < 3) return points.slice();
  // Closed contour: split at the two farthest-apart points so DP runs on two
  // open chains, mirroring how OpenCV approximates a closed curve.
  let i0 = 0, i1 = 0, maxD = -1;
  for (let i = 1; i < points.length; i++) {
    const d = dist(points[0], points[i]);
    if (d > maxD) {
      maxD = d;
      i1 = i;
    }
  }
  void i0;
  const chainA = points.slice(0, i1 + 1);
  const chainB = points.slice(i1).concat([points[0]]);
  const a = dpOpen(chainA, epsilon);
  const b = dpOpen(chainB, epsilon);
  // Stitch, dropping the duplicated shared endpoints.
  const out = a.slice(0, -1).concat(b.slice(0, -1));
  return out;
}

function dpOpen(points: Point[], epsilon: number): Point[] {
  if (points.length < 3) return points.slice();
  let maxDist = 0;
  let index = 0;
  const end = points.length - 1;
  for (let i = 1; i < end; i++) {
    const d = perpDistance(points[i], points[0], points[end]);
    if (d > maxDist) {
      maxDist = d;
      index = i;
    }
  }
  if (maxDist > epsilon) {
    const left = dpOpen(points.slice(0, index + 1), epsilon);
    const right = dpOpen(points.slice(index), epsilon);
    return left.slice(0, -1).concat(right);
  }
  return [points[0], points[end]];
}

function perpDistance(p: Point, a: Point, b: Point): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len = Math.hypot(dx, dy);
  if (len === 0) return dist(p, a);
  return Math.abs(dx * (a.y - p.y) - (a.x - p.x) * dy) / len;
}

/** True if a simple polygon is convex (mirrors cv2.isContourConvex). */
export function isConvex(pts: Point[]): boolean {
  const n = pts.length;
  if (n < 4) return true;
  let sign = 0;
  for (let i = 0; i < n; i++) {
    const a = pts[i];
    const b = pts[(i + 1) % n];
    const c = pts[(i + 2) % n];
    const cross = (b.x - a.x) * (c.y - b.y) - (b.y - a.y) * (c.x - b.x);
    if (cross !== 0) {
      const s = cross > 0 ? 1 : -1;
      if (sign === 0) sign = s;
      else if (s !== sign) return false;
    }
  }
  return true;
}

/** Order 4 points as TL, TR, BR, BL — the port of order_points(). */
export function orderPoints(pts: Point[]): [Point, Point, Point, Point] {
  // sum = x+y (min=TL, max=BR); diff = x-y in OpenCV's np.diff(axis=1) is y-x...
  // Python uses np.diff(pts, axis=1) = col1-col0 = y-x; argmin→TR, argmax→BL.
  let tl = pts[0], br = pts[0], tr = pts[0], bl = pts[0];
  let minSum = Infinity, maxSum = -Infinity, minDiff = Infinity, maxDiff = -Infinity;
  for (const p of pts) {
    const sum = p.x + p.y;
    const diff = p.y - p.x;
    if (sum < minSum) { minSum = sum; tl = p; }
    if (sum > maxSum) { maxSum = sum; br = p; }
    if (diff < minDiff) { minDiff = diff; tr = p; }
    if (diff > maxDiff) { maxDiff = diff; bl = p; }
  }
  return [tl, tr, br, bl];
}

/**
 * Reduce a convex hull to a 4-corner quad by the extreme-of-(x±y) heuristic —
 * the fallback when approxPolyDP doesn't land exactly on 4 points. The Python
 * pipeline only ever warps from a genuine 4-gon, so this is used internally by
 * the localizer's foreground path (which already starts from a min-area box).
 */
export function hullToQuad(hull: Point[]): [Point, Point, Point, Point] {
  return orderPoints(hull);
}
